import type { AutoLayout, CursorProtocol, Direction, LayoutPosition, LayoutSize, SizeConfigure } from '@lighthouse/core'
import { DIRECTION } from '@lighthouse/core'
import { css, FlattenSimpleInterpolation } from 'styled-components'

import { PAGE_CONTAINER_HOST } from '../constants'
import { getImageFullUrlInApplication } from './image'

export const flexMinSizeStyle: React.CSSProperties = {
    minWidth: 0,
    minHeight: 0
}

export const flexMinSizeCSS = (): FlattenSimpleInterpolation => css`
    min-width: 0;
    min-height: 0;
`

export function getFontSizeByPageStyle(defaultSize = 16) {
    const { fontSize } = window.getComputedStyle(document.documentElement)
    const match = /[\d.]+/.exec(fontSize)
    return match ? Number.parseFloat(match[0]) : defaultSize
}

export function getExplicitWidth(configure: SizeConfigure, parentBoxWidth: number, sortable = false): number {
    if (!configure || configure?.size === 'fill' || configure?.size === 'auto') {
        return parentBoxWidth
    }
    if (configure.unit === '%') {
        return parentBoxWidth * ((configure.size ?? 0) / 100)
    }
    if (configure.unit === 'vw') {
        const pageContainer = sortable ? document.querySelector<HTMLDivElement>(PAGE_CONTAINER_HOST) : null
        const containerWidth = pageContainer ? pageContainer.clientWidth : window.innerWidth
        return containerWidth * ((configure.size ?? 0) / 100)
    }
    if (configure.unit === 'rem') {
        const fontSize = getFontSizeByPageStyle() ?? 14
        return fontSize * (configure.size ?? 0)
    }
    return configure.size ?? 0
}

export function getExplicitHeight(configure: SizeConfigure, parentBoxHeight: number, sortable = false): number {
    if (!configure || configure?.size === 'fill' || configure?.size === 'auto') {
        return parentBoxHeight
    }
    if (configure.unit === 'vh') {
        const pageContainer = sortable ? document.querySelector<HTMLDivElement>(PAGE_CONTAINER_HOST) : null
        const containerHeight = pageContainer ? pageContainer.clientHeight : window.innerHeight
        return containerHeight * ((configure.size ?? 0) / 100)
    }
    if (configure.unit === '%') {
        return parentBoxHeight * ((configure.size ?? 0) / 100)
    }
    if (configure.unit === 'rem') {
        const fontSize = getFontSizeByPageStyle() ?? 14
        return fontSize * (configure.size ?? 0)
    }
    return configure.size ?? 0
}

export function getWidthSize(configure: SizeConfigure, parentDirection: Direction = DIRECTION.horizontal, sortable = false) {
    if (configure?.size === 'fill') {
        if (parentDirection === DIRECTION.horizontal) {
            return {
                flex: '1 0 0'
            }
        }

        return {
            width: '100%'
        }
    }

    if (configure?.size === 'auto') {
        if (parentDirection === DIRECTION.horizontal) {
            return {
                width: 'auto'
                /* flex: '0 0 auto' */
            }
        }
        return {}
    }
    if (sortable && configure.unit === 'vw') {
        const pageContainer = document.querySelector<HTMLDivElement>(PAGE_CONTAINER_HOST)
        if (!pageContainer) {
            return { width: `${configure.size}${configure.unit}` }
        }
        const size = configure.size ?? 0
        const pageContainerWidth = pageContainer.clientWidth ?? 0
        const width = Number(pageContainerWidth * (size / 100)).toFixed(2)
        return { width: `${width}px` }
    }

    if (sortable && configure.unit === 'rem') {
        const fontSize = getFontSizeByPageStyle()
        if (!fontSize) {
            return { width: `${configure.size}${configure.unit}` }
        }

        const remSize = configure.size || 0
        return { width: `${fontSize * remSize}px` }
    }

    return { width: `${configure?.size ?? 0}${configure?.unit || 'px'}` }
}
function getHeightSize(configure: SizeConfigure | undefined, parentDirection: Direction = DIRECTION.horizontal, sortable = false) {
    if (!configure) {
        return {}
    }
    if (configure?.size === 'fill') {
        if (parentDirection === DIRECTION.vertical) {
            return {
                flex: '1 0 0'
            }
        }

        return {
            height: '100%'
        }
    }

    if (configure?.size === 'auto') {
        if (parentDirection === DIRECTION.vertical) {
            return {
                height: 'auto'
            }
        }
        return {}
    }

    if (sortable && configure.unit === 'vh') {
        const pageContainer = document.querySelector<HTMLDivElement>(PAGE_CONTAINER_HOST)
        if (!pageContainer) {
            return { height: `${configure?.size ?? 0}${configure?.unit || 'px'}` }
        }
        const size = configure.size ?? 0
        const pageContainerHeight = pageContainer.clientHeight ?? 0
        const height = Number(pageContainerHeight * (size / 100)).toFixed(2)
        return { height: `${height}px` }
    }

    if (sortable && configure.unit === 'rem') {
        const fontSize = getFontSizeByPageStyle()
        if (!fontSize) {
            return { width: `${configure.size}${configure.unit}` }
        }

        const remSize = configure.size || 0

        return { height: `${fontSize * remSize}px` }
    }

    return { height: `${configure?.size ?? 0}${configure?.unit || 'px'}` }
}

function getMinMaxSize(configure: Partial<SizeConfigure> | undefined) {
    if (configure?.size === 'auto') {
        return undefined
    }
    if (configure?.size === 'fill') {
        return '100%'
    }
    if (configure?.size) {
        return `${configure.size}${configure?.unit || 'px'}`
    }
    return undefined
}

export function getSizeStyle(
    layoutSize?: LayoutSize,
    parentDirection: Direction = DIRECTION.horizontal,
    sortable = false
): React.CSSProperties {
    if (!layoutSize) {
        return {}
    }

    const sizeStyle: React.CSSProperties = {
        ...getWidthSize(layoutSize.width, parentDirection, sortable),
        minWidth: getMinMaxSize(layoutSize.minWidth) || 0,
        maxWidth: getMinMaxSize(layoutSize.maxWidth),
        ...getHeightSize(layoutSize.height, parentDirection, sortable),
        minHeight: getMinMaxSize(layoutSize.minHeight) || 0,
        maxHeight: getMinMaxSize(layoutSize.maxHeight)
    }
    // 注释掉node上的溢出隐藏, 会导致block上的阴影也被隐藏掉
    if (layoutSize.overflow === 'hidden') {
        sizeStyle.overflow = layoutSize.overflow
    }
    return sizeStyle
}

export function getLayoutStyle(layout?: AutoLayout, isBuilder?: boolean): React.CSSProperties {
    if (!layout) {
        return {}
    }

    const isVertical = layout.align?.direction === DIRECTION.vertical
    const isDistributedAlignment = !!layout.align?.isDistributedAlignment

    if (isBuilder) {
        let gap
        if (layout.gap?.unit === 'rem') {
            const fontSize = getFontSizeByPageStyle()
            if (fontSize) {
                gap = `${fontSize * (layout.gap.size || 0)}px`
            }
        } else {
            gap = layout.gap && `${layout.gap.size || 0}${layout.gap.unit || 'px'}`
        }

        return {
            display: 'flex',
            flexDirection: isVertical ? 'column' : 'row',
            justifyContent: isDistributedAlignment ? 'space-between' : isVertical ? layout.align?.alignY : layout.align?.alignX,
            alignItems: isVertical ? layout.align?.alignX : layout.align?.alignY,
            flexWrap: layout.wrap ? 'wrap' : undefined,
            gap
        }
    }

    return {
        display: 'flex',
        flexDirection: isVertical ? 'column' : 'row',
        justifyContent: isDistributedAlignment ? 'space-between' : isVertical ? layout.align?.alignY : layout.align?.alignX,
        alignItems: isVertical ? layout.align?.alignX : layout.align?.alignY,
        flexWrap: layout.wrap ? 'wrap' : undefined,
        gap: layout.gap && `${layout.gap.size || 0}${layout.gap.unit || 'px'}`
    }
}

export function getPositionStyle(position?: LayoutPosition, level = 1): React.CSSProperties {
    if (!position || !position.type) {
        return {
            position: 'relative'
        }
    }
    if (position.type === 'relative') {
        return {
            position: 'relative'
        }
    }
    const verticalKey = position.location?.vertical === 'bottom' ? 'bottom' : 'top'
    if (position.type === 'sticky') {
        return {
            position: 'sticky',
            [verticalKey]: position.location?.verticalValue,
            zIndex: level + 1
        }
    }

    const horizontalKey = position.location?.horizontal === 'right' ? 'right' : 'left'

    return {
        position: position.type,
        [horizontalKey]: position.location?.horizontalValue,
        [verticalKey]: position.location?.verticalValue,
        zIndex: level + 1
    }
}

export function getCursorStyle(appId: string, cursor?: CursorProtocol): React.CSSProperties {
    if (!cursor || !cursor.enable) {
        return {}
    }

    if (cursor.type === 'custom') {
        if (!cursor.url) {
            return {}
        }
        return { cursor: `url(${getImageFullUrlInApplication(appId, cursor.url)}), auto` }
    }

    return { cursor: cursor.value }
}
